import type { AxiosResponse } from 'axios';
import { milliseconds } from 'date-fns';
import _ from 'lodash';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import V2Error from '@/app/V2Error';
import api from '@/services/api/v2';
import { clientKey } from '@/services/queryKey';
import { ListQueryParamEnum } from '@/types/enums';

interface ClientListQueryParams
  extends Partial<Record<ListQueryParamEnum, string | number>> {
  search?: string;
  all?: boolean;
}

interface ClientListQueryParams {
  q?: string;
  page?: number | string;
  size?: number | string;
  needAll?: '0' | '1' | 0 | 1;
}

export const clientService = {
  async fetchClientListByScroll({
    search,
    all,
    ...rest
  }: ClientListQueryParams) {
    try {
      const { data } = await api.get<
        ListResponse<
          {
            clientId: number;
            companyName: string;
          }[]
        >
      >(`/clients/scroll`, {
        params: {
          q: search,
          needAll: all && 1,
          ...rest,
        },
      });
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },
  /**
   * 입력 받는 리스팅 파라미터에 따른 고객사 리스트를 요청합니다.
   */
  async fetchClientList({ search, ...rest }: ClientListQueryParams) {
    const { data } = await api.get<{ content: Client.Item[] } & Pagination>(
      '/clients/card',
      {
        params: {
          q: search,
          ...rest,
        },
      }
    );

    return data;
  },
  /**
   * 입력 받는 지점 키에 따라 지점 상세 데이터를 받아옵니다.
   */
  async fetchClientDetail(clientId: number) {
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },

  /**
   * 고객사 업무 대시보드 패널에 보여줄 고객사 - 지점 리스트를 보여줍니다.
   */
  async fetchClientListForPanel() {
    const { data } = await api.get('/clients/panel');
    return data;
  },

  async createClient(payload: ClientForm.Payload) {
    return await api.post('/clients', payload);
  },

  async updateClient(clientId: number, payload: ClientForm.Payload) {
    return await api.put(`/clients/${clientId}`, payload);
  },
};

export function useFetchClientListByScroll(params: ClientListQueryParams = {}) {
  const { data } = useQuery(
    clientKey.listByScroll({ ...params }),
    () => clientService.fetchClientListByScroll(params),
    {
      staleTime: milliseconds({ minutes: 1 }),
    }
  );
  return data;
}

export function useFetchClientList(params: ClientListQueryParams = {}) {
  const { isLoading, isFetching, data, dataUpdatedAt, isError, refetch } =
    useQuery(
      // ['client', 'list', ...queryKeys],
      clientKey.listByPage({ ...params }),
      () => clientService.fetchClientList(params),
      {
        staleTime: milliseconds({ minutes: 1 }),
        retry: false,
        keepPreviousData: true,
        suspense: false,
        useErrorBoundary: false,
      }
    );

  return {
    isLoading,
    isFetching,
    source: data,
    sourcesUpdatedAt: dataUpdatedAt,
    isError,
    refetch,
  };
}

export function useAsyncCreateClient(onSuccess, onError) {
  const { mutate } = useMutation(
    (payload: ClientForm.Payload) => clientService.createClient(payload),
    {
      onSuccess(res) {
        onSuccess && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateClient(
  clientId: number,
  onSuccess: (data: any) => void,
  onError: (err: V2ErrorType) => void
) {
  const { mutate } = useMutation<
    AxiosResponse,
    V2ErrorType,
    ClientForm.Payload
  >((payload) => clientService.updateClient(clientId, payload), {
    onSuccess(res) {
      onSuccess && onSuccess(res.data);
    },
    onError(err) {
      onError && onError(err);
    },
  });

  return mutate;
}

export function useFetchClientDetail(clientId: number) {
  if (clientId && !Number.isInteger(clientId)) {
    throw new TypeError(`'clientId' is not a valid integer.`);
  }

  const { data, isLoading, isFetching, isError, refetch } = useQuery<
    Client.Detail,
    V2Error
  >(
    clientKey.detailById(clientId),
    () => clientService.fetchClientDetail(clientId),
    {
      enabled: !!clientId && Number.isInteger(clientId),
      retry: false,
    }
  );

  return {
    isLoading,
    isFetching,
    isError,
    source: data,
    refetch,
  };
}

export function useFetchClientListForPanel() {
  return useQuery<ListResponse<Client.PanelItem[]>, V2Error>(
    clientKey.listWithVenueListByScroll(),
    () => clientService.fetchClientListForPanel(),
    {
      staleTime: milliseconds({ seconds: 10 }),
      suspense: false,
      useErrorBoundary: false,
    }
  );
}

export function useFetchClientInfiniteList(params: ClientListQueryParams) {
  const {
    data,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery(
    clientKey.listByScroll({ ...params }),
    ({ pageParam }) =>
      clientService.fetchClientListByScroll({ ...params, page: pageParam }),
    {
      keepPreviousData: true,
      suspense: false,
      useErrorBoundary: false,
      getNextPageParam: (page) => {
        return page.last ? undefined : page.number + 1;
      },
    }
  );

  return {
    sources: data?.pages,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    status,
    refetch,
  };
}
